import { nextBoundaryMs, resolvePosition, SLOT_MS } from "./clock.js";
import type { Narrator, PreparedNarration, PlaybackHandle } from "../audio/narrator.js";
import { DUCK_IN_SEC, DUCK_OUT_SEC, FIXED_BED_GAIN, scheduleDuck } from "../audio/duck.js";
import type { EngineNodes } from "../audio/engine.js";

/**
 * Player: the wall-clock state machine.
 *
 *   idle -> gate -> loading-model -> fetching -> playing -> error
 *
 * The station keeps playing while the tab is hidden. `suspend()`/`resume()`
 * still exist and are correct, but nothing in the shipping bootstrap calls them
 * on `visibilitychange` any more: background playback is the point of an
 * ambient station. They remain the seam that a future "pause when hidden"
 * setting would use, and the browser tests drive them directly.
 *
 * There is no pause, skip, or seek. The only transition a user can trigger is
 * the initial gate. Space activates that gate once and is inert afterwards.
 *
 * Three rules with no override:
 *   1. Recompute position immediately before every play(). A slow prepare must
 *      never play at an offset captured seconds earlier, and a callback that
 *      runs across a boundary must not resurrect the slot that just ended.
 *   2. A prepared buffer whose slot has passed is always discarded. This is what
 *      makes hidden-tab playback safe: timer throttling can fire a boundary
 *      late, and this rule turns that into a late boundary rather than stale
 *      audio.
 *   3. A boundary timer is never left unarmed. Suspension cancels it; resume
 *      re-arms it, and so does returning to a hidden tab.
 */

export type PlayerState =
  | "idle"
  | "gate"
  | "loading-model"
  | "fetching"
  | "playing"
  | "suspended"
  | "error";

/** How far the running narration may drift before it is restarted in place. */
const RESYNC_THRESHOLD_SEC = 1.5;
/** Do not resync into the last moments of an entry; there is nothing to rescue. */
const RESYNC_MIN_REMAINING_SEC = 3;
/** Minimum wall-clock gap between two resyncs of the same entry. */
const RESYNC_COOLDOWN_MS = 5_000;

/**
 * How close to the slot boundary a non-seekable narrator must be before it is
 * allowed to start speaking.
 *
 * This exists to absorb timer lateness (the boundary timer is armed 25 ms past
 * the boundary), not to admit arbitrary mid-slot joins. The bound is tight
 * because the divergence it permits is the divergence every listener hears:
 * 250 ms is well below speech-synthesis start jitter, while an earlier draft's
 * one-second window would let a listener joining 900 ms in hear an entry from
 * its beginning that an earlier listener already heard from its beginning — the
 * exact drift the non-seekable path exists to avoid.
 */
const NON_SEEKABLE_BOUNDARY_TOLERANCE_SEC = 0.25;

/** The boundary timer fires this far past the boundary, absorbing clock granularity. */
const BOUNDARY_PAD_MS = 25;
/** Never arm a boundary timer closer than this, or it spins. */
const MIN_TIMER_DELAY_MS = 20;

export interface PlayerStatus {
  state: PlayerState;
  slotIndex: number | null;
  entryId: string | null;
  message: string;
}

export interface PlayerDeps {
  now: () => number;
  engine: EngineNodes;
  narrator: Narrator;
  /**
   * The audio clock used to measure how far narration actually advanced.
   *
   * Defaults to the AudioContext's own `currentTime`. It is injectable because
   * drift is only meaningful as audio-position versus wall-clock-position: a
   * suspended context keeps its clock still while wall time advances, and an
   * offline test context never advances without rendering. Measuring both sides
   * on the wall clock makes the comparison tautologically zero.
   */
  audioTime?: () => number;
  /** Resolves the prepared narration for a slot (scheduler-backed). */
  getPrepared: (slotIndex: number) => PreparedNarration | null;
  requestPreparation: () => void;
  onStatus: (status: PlayerStatus) => void;
  onSlotChange: (info: { slotIndex: number; entryId: string | null; offsetSec: number }) => void;
  scheduleTimer?: (callback: () => void, delayMs: number) => number;
  cancelTimer?: (handle: number) => void;
  slotMs?: number;
}

export class Player {
  private state: PlayerState = "idle";
  private timer: number | null = null;
  private handle: PlaybackHandle | null = null;
  private currentSlot: number | null = null;
  private currentEntryId: string | null = null;
  private suspendedFrom: PlayerState | null = null;
  private disposed = false;
  /** Narration offset (seconds) at which the current playback began. */
  private startedOffsetSec = 0;
  /** Audio-clock instant at which the current playback began. */
  private startedAtAudioSec = 0;
  private lastResyncAtMs = 0;
  /** The last slot this player observed, for detecting genuine boundary crossings. */
  private lastObservedSlot: number | null = null;
  /**
   * The slot we entered at its boundary. Only this slot may be spoken by a
   * non-seekable narrator; every other slot was joined mid-flight.
   */
  private boundarySlot: number | null = null;
  /** Bumped on every start/stop so a superseded `onEnd` cannot restore audio. */
  private generation = 0;

  constructor(private readonly deps: PlayerDeps) {}

  get status(): PlayerStatus {
    return {
      state: this.state,
      slotIndex: this.currentSlot,
      entryId: this.currentEntryId,
      message: this.state === "playing" && this.currentEntryId ? this.currentEntryId : "",
    };
  }

  get isPlaying(): boolean {
    return this.state === "playing";
  }

  setState(state: PlayerState, message = ""): void {
    this.state = state;
    this.deps.onStatus({
      state,
      slotIndex: this.currentSlot,
      entryId: this.currentEntryId,
      message,
    });
  }

  /** Called once, from the user gesture. Idempotent from the player's side. */
  start(): void {
    if (this.state !== "idle" && this.state !== "gate") return;
    this.setState("playing");
    this.evaluate();
    this.armBoundary();
  }

  /**
   * Evaluate the current instant and begin the slot if a fresh, matching
   * narration exists. Called at boundaries and whenever preparation completes.
   */
  evaluate(): void {
    if (this.disposed) return;
    if (this.state !== "playing" && this.state !== "fetching" && this.state !== "loading-model") {
      return;
    }

    // A pass can straddle a boundary: it reads the position, does synchronous
    // work, then reads again and finds a different slot. Returning there would
    // leave the slot that was entered unevaluated, so it would air silent until
    // the *following* boundary — a full avoidable slot of dead air even when
    // that slot's narration is already prepared. One extra pass fixes it.
    // Bounded at two so a pathological clock cannot loop.
    if (this.evaluatePass()) this.evaluatePass();
  }

  /**
   * One evaluation pass. Returns true when a slot boundary was crossed partway
   * through and the new slot has not been evaluated yet.
   */
  private evaluatePass(): boolean {
    if (this.disposed) return false;
    const slotMs = this.deps.slotMs ?? SLOT_MS;
    const { slotIndex, offsetSec } = resolvePosition(this.deps.now(), slotMs);

    // Detect a boundary crossing by comparing observed slot indices. Inferring
    // it from the offset would treat a late timer, or a listener joining a few
    // hundred milliseconds in, as a boundary arrival.
    if (this.lastObservedSlot === null) {
      if (offsetSec <= NON_SEEKABLE_BOUNDARY_TOLERANCE_SEC) this.boundarySlot = slotIndex;
    } else if (slotIndex !== this.lastObservedSlot) {
      this.boundarySlot = slotIndex;
    }
    this.lastObservedSlot = slotIndex;

    if (this.currentSlot !== slotIndex) {
      // The slot rolled over: whatever is playing belongs to the past slot.
      this.stopPlayback();
      this.currentSlot = slotIndex;
      this.currentEntryId = null;
    }

    const prepared = this.deps.getPrepared(slotIndex);
    this.deps.onSlotChange({ slotIndex, entryId: prepared?.entryId ?? null, offsetSec });
    this.deps.requestPreparation();

    if (!prepared || prepared.slotIndex !== slotIndex) {
      this.setState("playing");
      return this.slotChangedSince(slotIndex, slotMs);
    }

    if (this.handle && this.currentEntryId === prepared.entryId) {
      this.resyncIfDrifted(prepared, slotIndex, slotMs);
      return this.slotChangedSince(slotIndex, slotMs);
    }

    if (!this.deps.narrator.capabilities.seekable) {
      this.startNonSeekable(prepared, slotIndex, slotMs);
      return this.slotChangedSince(slotIndex, slotMs);
    }

    // Freshly recomputed offset, not one captured earlier in this call.
    const fresh = resolvePosition(this.deps.now(), slotMs);
    if (fresh.slotIndex !== slotIndex) {
      // The boundary was crossed while this evaluation was running. Starting
      // now would air a slot that has already ended.
      this.stopPlayback();
      this.setState("playing");
      return true;
    }
    if (fresh.offsetSec >= prepared.durationSec - 0.05) {
      // Nothing left of this entry; the slot is legitimately silent narration.
      this.setState("playing");
      return false;
    }
    this.beginPlayback(prepared, fresh.offsetSec);
    return this.slotChangedSince(slotIndex, slotMs);
  }

  /** Did the wall clock move into a different slot during this pass? */
  private slotChangedSince(slotIndex: number, slotMs: number): boolean {
    return resolvePosition(this.deps.now(), slotMs).slotIndex !== slotIndex;
  }

  /**
   * A running playback may have drifted from wall-clock position (a slow start,
   * a resumed tab, a long microtask). Resynchronizing restarts it at the
   * correct offset, which the seekable contract allows.
   *
   * Guarded: only resync when the drift can actually be heard, only when there
   * is audio left worth rescuing, and only at a bounded rate. Without the rate
   * limit, a narration whose remaining audio runs out (so it stops advancing)
   * would be restarted forever, re-playing the same tail every evaluation.
   */
  private resyncIfDrifted(prepared: PreparedNarration, slotIndex: number, slotMs: number): void {
    // Resyncing is a seek. A non-seekable narrator cannot start mid-slot, so
    // "restarting at the correct offset" would silently mean "restart from the
    // beginning" — the exact desynchronization the non-seekable path exists to
    // avoid. Let the utterance run to its end instead.
    if (!this.deps.narrator.capabilities.seekable) {
      this.setState("playing");
      return;
    }

    const now = this.deps.now();
    const fresh = resolvePosition(now, slotMs);
    if (fresh.slotIndex !== slotIndex) {
      // The slot ended while we were deciding. Never restart it.
      this.stopPlayback();
      this.setState("playing");
      return;
    }

    // Compare the narration's actual position against the wall clock. Both
    // sides must come from different clocks: measuring the audio advance with
    // `now()` as well would compare wall time to wall time and always report
    // zero drift, so the resync could never fire.
    const audioElapsedSec = this.audioElapsedSinceStart();
    const narrationPosSec = this.startedOffsetSec + audioElapsedSec;
    const drift = Math.abs(narrationPosSec - fresh.offsetSec);
    const canResync =
      drift > RESYNC_THRESHOLD_SEC &&
      fresh.offsetSec < prepared.durationSec - RESYNC_MIN_REMAINING_SEC &&
      now - this.lastResyncAtMs > RESYNC_COOLDOWN_MS;

    if (canResync) {
      this.lastResyncAtMs = now;
      this.beginPlayback(prepared, fresh.offsetSec);
    }
    this.setState("playing");
  }

  /** Audio-clock seconds elapsed since the current playback began. */
  private audioElapsedSinceStart(): number {
    const audioNow = (this.deps.audioTime ?? (() => this.deps.engine.context.currentTime))();
    return Math.max(0, audioNow - this.startedAtAudioSec);
  }

  /**
   * A non-seekable narrator cannot start mid-slot, so it speaks only from a
   * boundary it actually observed. Joining mid-slot means silence until the
   * next boundary, which is what keeps every listener at the same point.
   */
  private startNonSeekable(prepared: PreparedNarration, slotIndex: number, slotMs: number): void {
    if (this.boundarySlot !== slotIndex) {
      this.setState("playing");
      return;
    }
    const fresh = resolvePosition(this.deps.now(), slotMs);
    if (fresh.slotIndex !== slotIndex) {
      this.stopPlayback();
      this.setState("playing");
      return;
    }
    if (fresh.offsetSec > NON_SEEKABLE_BOUNDARY_TOLERANCE_SEC) {
      // The boundary is too far behind us to claim we are starting at it.
      // Silence for the rest of the slot is the honest degradation.
      this.setState("playing");
      return;
    }
    this.beginPlayback(prepared, 0);
  }

  private beginPlayback(prepared: PreparedNarration, offsetSec: number): void {
    // Stop without restoring the music bed: the duck scheduled below owns the
    // music gain for the duration of this narration.
    this.stopPlayback({ restoreMusic: false });

    const context = this.deps.engine.context;
    const now = context.currentTime;
    const duck = this.deps.engine.musicDuck.gain;
    const duckable = this.deps.narrator.capabilities.duckable && prepared.buffer !== undefined;

    if (duckable) {
      scheduleDuck(duck, now + 0.02, Math.max(0, prepared.durationSec - offsetSec));
    } else {
      // Web Speech cannot be sidechained, so the bed is lowered to a fixed
      // level for the utterance instead. Begin from the current value and ramp
      // in, so the change is not a step discontinuity.
      duck.cancelScheduledValues(now);
      duck.setValueAtTime(duck.value, now);
      duck.linearRampToValueAtTime(FIXED_BED_GAIN, now + DUCK_IN_SEC);
    }

    const generation = this.nextGeneration();
    // Only a *new* entry gets a fresh resync budget. Resetting it on a resync of
    // the same entry would defeat the cooldown that stops an exhausted buffer
    // from being replayed every evaluation.
    if (this.currentEntryId !== prepared.entryId) this.lastResyncAtMs = 0;
    this.currentEntryId = prepared.entryId;
    // Narration clock reference, so a later evaluation can resynchronize. The
    // wall clock and the audio clock are captured separately: drift is the
    // difference between them, not a wall-clock-only identity.
    this.startedOffsetSec = offsetSec;
    this.startedAtAudioSec = (this.deps.audioTime ?? (() => context.currentTime))();

    // A narrator that throws from `play()` must not strand the music bed or the
    // state machine. The duck above is already scheduled, and without a handle
    // there is no `onEnd` to release it, so a throw would leave the bed lowered
    // for good. Reported as an honest error state rather than swallowed.
    try {
      this.handle = this.deps.narrator.play(prepared, {
        atOffsetSec: offsetSec,
        sync: { contextTime: now + 0.02, narrationTimeSec: offsetSec },
        onEnd: () => {
          // A handle superseded by a later playback must not touch shared state:
          // its duck automation has already been replaced.
          if (generation !== this.generation) return;
          this.handle = null;
          this.restoreMusic();
        },
      });
    } catch (error) {
      this.handle = null;
      this.restoreMusic();
      this.currentEntryId = null;
      this.setState("playing");
      console.error("narrator.play() failed; continuing music-only.", error);
      return;
    }
    this.setState("playing");
  }

  /** Cancel any duck and return the music bus to unity over the slow release. */
  private restoreMusic(): void {
    const context = this.deps.engine.context;
    const duck = this.deps.engine.musicDuck.gain;
    try {
      const now = context.currentTime;
      duck.cancelScheduledValues(now);
      duck.setValueAtTime(duck.value, now);
      duck.linearRampToValueAtTime(1, now + DUCK_OUT_SEC);
    } catch {
      // A closed context cannot take automation. Nothing left to restore.
    }
  }

  private nextGeneration(): number {
    this.generation += 1;
    return this.generation;
  }

  /**
   * Stop the current playback. Restoring the music is the default because the
   * stop path is shared: only `beginPlayback` hands ownership of the music gain
   * to a new duck.
   *
   * `WebSpeechNarrator.stop()` cancels the utterance without invoking `onEnd`,
   * so without restoring here a boundary-cancelled utterance would leave the
   * bed pinned at the fixed lowered level forever.
   */
  private stopPlayback(options: { restoreMusic?: boolean } = {}): void {
    const restoreMusic = options.restoreMusic ?? true;
    this.nextGeneration();
    if (this.handle) {
      try {
        this.handle.stop();
      } catch {
        // A narrator that already ended throws nothing of consequence.
      }
      this.handle = null;
    }
    if (restoreMusic) this.restoreMusic();
  }

  private clearTimer(): void {
    if (this.timer === null) return;
    const cancel = this.deps.cancelTimer ?? ((handle: number) => window.clearTimeout(handle));
    cancel(this.timer);
    this.timer = null;
  }

  /** Idempotent: any previously armed boundary is cancelled first. */
  private armBoundary(): void {
    if (this.disposed) return;
    this.clearTimer();
    const slotMs = this.deps.slotMs ?? SLOT_MS;
    const schedule = this.deps.scheduleTimer ?? ((cb, ms) => window.setTimeout(cb, ms));
    const now = this.deps.now();
    const target = nextBoundaryMs(now, slotMs);
    const delay = Math.max(MIN_TIMER_DELAY_MS, target - now + BOUNDARY_PAD_MS);
    this.timer = schedule(() => {
      this.timer = null;
      if (this.disposed) return;
      // Suspension cancels the timer, so this is defence against a callback
      // that was already queued. `resume()` arms a fresh boundary either way,
      // so the timer chain never dies here.
      if (this.state === "suspended") return;
      try {
        this.evaluate();
      } finally {
        // The timer chain is the station's heartbeat. An exception from one
        // evaluation must not be the last thing that ever happens to it, or a
        // single bad frame converts into permanent silence.
        this.armBoundary();
      }
    }, delay);
  }

  suspend(): void {
    if (this.state === "suspended" || this.state === "idle") return;
    this.suspendedFrom = this.state;
    // Cancel rather than let a stale boundary fire into a hidden tab: a
    // callback that fires while suspended must not be the last thing that
    // happens to the timer chain.
    this.clearTimer();
    this.stopPlayback();
    this.state = "suspended";
  }

  async resume(): Promise<void> {
    if (this.state !== "suspended") return;
    this.state = this.suspendedFrom ?? "playing";
    this.suspendedFrom = null;
    // Recompute: the slot may have rolled over while hidden.
    this.evaluate();
    this.armBoundary();
  }

  dispose(): void {
    this.disposed = true;
    this.clearTimer();
    // The engine owns the context and closes it after this returns; do not
    // schedule automation against it on the way out.
    this.stopPlayback({ restoreMusic: false });
  }
}
